import { supabase, generateGeohash } from '../config/supabase';
import type { AnalyticsEvent, OrganizerZone } from '../config/supabase';
// NOTE: Firebase Firestore import supprimé - le count d'events est maintenant
// toujours passé via le paramètre publishedEventsCount depuis OrganizerContext

/**
 * Service d'analytics spécialement conçu pour le dashboard organisateur
 * Récupère les données depuis Supabase avec sécurité RLS
 */
/**
 * Interfaces pour les types de données
 */
export interface DashboardKPIs {
  connectionsZone: number | null; // null = zone non configurée
  apparitionsEvents: number;
  clicsEvents: number;
  likesEvents: number;
  eventsPublies: number; // Depuis Firebase (pas Supabase)
  hasZone: boolean; // Indique si la zone organisateur est configurée
}

export interface DashboardData {
  kpis: DashboardKPIs;
  trends: {
    connectionsZone: { value: number; type: 'up' | 'down' };
    apparitionsEvents: { value: number; type: 'up' | 'down' };
    clicsEvents: { value: number; type: 'up' | 'down' };
    likesEvents: { value: number; type: 'up' | 'down' };
  };
  insights: Array<{
    type: 'insight' | 'conseil';
    title: string;
    message: string;
  }>;
}

// Types pour le sélecteur de période
export type AnalyticsPeriod = 'alltime' | '7days' | '1month';

export class OrganizerAnalyticsService {

  /**
   * Configuration par défaut
   */
  private static readonly CONFIG = {
    // Périodes disponibles (en jours, null = all time)
    PERIODS: {
      'alltime': null,
      '7days': 7,
      '1month': 30
    } as const,

    // Au-delà de ce nombre d'event ids, les requêtes GET `.in('event_id', ids)`
    // dépassent les limites de longueur d'URL (ex. 441 events ≈ 17 KB) et peuvent
    // échouer silencieusement : on bascule sur le filtre organizer_id (un seul eq),
    // qui donne les mêmes totaux globaux (les lignes analytics portent organizer_id).
    MAX_EVENT_IDS_FOR_IN_FILTER: 100,

    // Seuils pour les insights automatiques
    THRESHOLDS: {
      HIGH_ACTIVITY: 100,   // Seuil connexions "élevées"
      LOW_CONVERSION: 0.05, // Seuil conversion faible (5%)
      WEEKEND_BOOST: 2.0    // Multiplier weekend vs semaine
    }
  };

  /**
   * Récupérer toutes les données du dashboard pour un organisateur
   * @param organizerId - ID de l'organisateur
   * @param publishedEventsCount - Nombre d'events publiés (optionnel)
   * @param period - Période d'analyse : '7days' ou '1month' (défaut: '7days')
   * @param eventIds - IDs des events de l'organisateur (pour requêtes optimisées)
   */
  static async getDashboardData(
    organizerId: string,
    publishedEventsCount?: number,
    period: AnalyticsPeriod = '7days',
    eventIds?: string[]
  ): Promise<DashboardData> {
    try {
      const periodDays = this.CONFIG.PERIODS[period];
      const isAllTime = periodDays === null;
      console.log(`📊 Récupération dashboard pour organisateur: ${organizerId} (période: ${period}${isAllTime ? '' : ` = ${periodDays} jours`})`);

      // Gros organisateurs : trop d'ids pour un filtre IN en GET → fallback organizer_id
      if (eventIds && eventIds.length > this.CONFIG.MAX_EVENT_IDS_FOR_IN_FILTER) {
        console.log(`ℹ️ ${eventIds.length} events > ${this.CONFIG.MAX_EVENT_IDS_FOR_IN_FILTER} : requêtes par organizer_id (limite d'URL)`);
        eventIds = undefined;
      }

      // 1. Vérifier/créer la zone organisateur (optionnel - ne bloque plus)
      const organizerZone = await this.ensureOrganizerZone(organizerId);
      const hasZone = !!organizerZone;

      if (!hasZone) {
        console.log(`ℹ️ Zone non configurée pour ${organizerId} - KPIs events seront récupérés sans zone`);
      }

      // 2. Récupérer les KPIs actuels (période sélectionnée)
      // Les KPIs des events fonctionnent même sans zone
      const currentKPIs = await this.getKPIsForPeriod(
        organizerId,
        organizerZone?.geohash || null,
        periodDays,
        0, // offsetDays
        publishedEventsCount,
        hasZone,
        eventIds
      );

      // 3. Récupérer les KPIs précédents (période équivalente précédente pour comparaison)
      // Pour "all time", pas de comparaison de tendances
      let previousKPIs: DashboardKPIs;
      if (isAllTime) {
        // Pas de période précédente pour all time
        previousKPIs = { ...currentKPIs };
      } else {
        previousKPIs = await this.getKPIsForPeriod(
          organizerId,
          organizerZone?.geohash || null,
          periodDays,
          periodDays!, // offset = même durée que la période actuelle
          publishedEventsCount,
          hasZone,
          eventIds
        );
      }

      // 4. Calculer les tendances (pas de trends pour all time)
      const trends = isAllTime
        ? this.getDefaultTrends()
        : this.calculateTrends(currentKPIs, previousKPIs);

      // 5. Générer les insights automatiques
      const insights = this.generateInsights(currentKPIs, trends, hasZone);

      return {
        kpis: currentKPIs,
        trends,
        insights
      };

    } catch (error) {
      console.error('❌ Erreur récupération dashboard:', error);

      // Retourner des données par défaut en cas d'erreur
      return this.getDefaultDashboardData();
    }
  }

  /**
   * Récupérer les KPIs pour une période donnée
   */
  private static async getKPIsForPeriod(
    organizerId: string,
    zoneKey: string | null,
    periodDays: number | null,
    offsetDays: number = 0,
    publishedEventsCount?: number,
    hasZone: boolean = false,
    eventIds?: string[]
  ): Promise<DashboardKPIs> {

    // Si periodDays est null (all time), pas de filtrage par date
    let startDateStr: string | null = null;
    let endDateStr: string | null = null;

    if (periodDays !== null) {
      const endDate = new Date();
      endDate.setDate(endDate.getDate() - offsetDays);

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - periodDays - offsetDays);

      startDateStr = startDate.toISOString().split('T')[0];
      endDateStr = endDate.toISOString().split('T')[0];

      console.log(`📅 Période: ${startDateStr} → ${endDateStr}`);
    } else {
      console.log(`📅 Période: All time`);
    }

    // Exécuter les requêtes en parallèle
    // connectionsZone seulement si on a une zone configurée
    // Si on a des eventIds, utiliser la méthode optimisée (par event_id), sinon fallback sur organizer_id
    const [connectionsZone, apparitionsEvents, clicsEvents, likesEvents] = await Promise.all([
      hasZone && zoneKey
        ? this.getConnectionsInZone(zoneKey, startDateStr, endDateStr)
        : Promise.resolve(null),
      this.getEventAppearances(organizerId, startDateStr, endDateStr, eventIds),
      this.getEventClicks(organizerId, startDateStr, endDateStr, eventIds),
      this.getEventLikes(organizerId, startDateStr, endDateStr, eventIds)
    ]);

    // Pour les événements publiés, utiliser le count local ou Firebase Firestore
    const eventsPublies = publishedEventsCount !== undefined
      ? publishedEventsCount
      : await this.getPublishedEventsCount(organizerId);

    return {
      connectionsZone,
      apparitionsEvents,
      clicsEvents,
      likesEvents,
      eventsPublies,
      hasZone
    };
  }

  /**
   * KPI 1: Connexions dans la zone (~15km autour de l'organisateur)
   * Utilise les 9 zones adjacentes (centre + 8 voisins) précision 5
   * Couvre ~15km x 15km pour compenser les imprécisions GPS
   *
   * Compte les deux types d'events de zone :
   * - debut_session : connexion initiale avec géolocalisation
   * - connexion_zone : recherche/navigation dans une zone
   *
   * ✅ NOUVEAU : Requête sur 3 niveaux (hot/warm/cold)
   * - analytics_events (< 24h) → données brutes
   * - analytics_daily (1-30 jours) → agrégé par jour
   * - analytics_alltime (> 30 jours) → cumulé
   */
  private static async getConnectionsInZone(
    geohash: string,
    startDate: string | null,
    endDate: string | null
  ): Promise<number> {

    // Utiliser le préfixe de précision 5 pour couvrir ~4.9km x 4.9km par cellule
    const geohashPrefix = geohash.substring(0, 5);

    // Obtenir les 9 zones adjacentes (centre + 8 voisins)
    const ngeohash = require('ngeohash');
    const neighbors = ngeohash.neighbors(geohashPrefix);
    const allZones = [geohashPrefix, ...neighbors]; // 9 zones au total

    const orConditions = allZones.map(zone => `geohash.like.${zone}%`).join(',');

    // === Niveau 1 : analytics_events (< 24h) - uniquement connexion_zone ===
    let eventsQuery = supabase
      .from('analytics_events')
      .select('*', { count: 'exact', head: true })
      .eq('event_name', 'connexion_zone')
      .or(orConditions);

    if (startDate && endDate) {
      eventsQuery = eventsQuery.gte('day_key', startDate).lte('day_key', endDate);
    }

    // === Niveau 2 : analytics_daily (1-30 jours) ===
    let dailyQuery = supabase
      .from('analytics_daily')
      .select('connexions_zone')
      .or(orConditions);

    if (startDate && endDate) {
      dailyQuery = dailyQuery.gte('date', startDate).lte('date', endDate);
    }

    // === Niveau 3 : analytics_alltime (> 30 jours) ===
    let alltimeQuery = supabase
      .from('analytics_alltime')
      .select('connexions_zone, first_date, last_date')
      .or(orConditions);

    if (startDate && endDate) {
      alltimeQuery = alltimeQuery.lte('first_date', endDate).gte('last_date', startDate);
    }

    // === Exécuter les 3 requêtes en parallèle ===
    const [eventsResult, dailyResult, alltimeResult] = await Promise.all([
      eventsQuery,
      dailyQuery,
      alltimeQuery
    ]);

    if (eventsResult.error) console.error('❌ Erreur events connexion_zone:', eventsResult.error);
    if (dailyResult.error) console.error('❌ Erreur daily:', dailyResult.error);
    if (alltimeResult.error) console.error('❌ Erreur alltime:', alltimeResult.error);

    // === Agréger les résultats ===
    let total = 0;

    // analytics_events (count direct)
    total += eventsResult.count || 0;

    // analytics_daily (somme connexions_zone)
    if (dailyResult.data) {
      total += dailyResult.data.reduce((sum: number, row: any) => sum + (row.connexions_zone || 0), 0);
    }

    // analytics_alltime (somme connexions_zone)
    if (alltimeResult.data) {
      total += alltimeResult.data.reduce((sum: number, row: any) => sum + (row.connexions_zone || 0), 0);
    }

    console.log(`📍 [3-tier] Connexions zone TOTAL: ${total}`);
    return total;
  }

  /**
   * KPI 2: Apparitions des événements de l'organisateur
   * Utilise eventIds si disponible (plus fiable), sinon fallback sur organizer_id
   * ✅ NOUVEAU : Requête sur 3 niveaux (hot/warm/cold)
   */
  private static async getEventAppearances(
    organizerId: string,
    startDate: string | null,
    endDate: string | null,
    eventIds?: string[]
  ): Promise<number> {

    let total = 0;

    // === Niveau 1 : analytics_events (< 24h) ===
    if (eventIds && eventIds.length > 0) {
      let query = supabase
        .from('analytics_events')
        .select('*', { count: 'exact', head: true })
        .eq('event_name', 'apparition_evenement')
        .in('event_id', eventIds);

      if (startDate && endDate) {
        query = query.gte('day_key', startDate).lte('day_key', endDate);
      }

      const { count, error } = await query;
      if (error) console.error('❌ Erreur events apparitions (by eventIds):', error);
      total += count || 0;
      console.log(`   - events.apparitions (eventIds): ${count || 0}`);
    } else {
      let query = supabase
        .from('analytics_events')
        .select('*', { count: 'exact', head: true })
        .eq('event_name', 'apparition_evenement')
        .eq('organizer_id', organizerId);

      if (startDate && endDate) {
        query = query.gte('day_key', startDate).lte('day_key', endDate);
      }

      const { count, error } = await query;
      if (error) console.error('❌ Erreur events apparitions (by organizerId):', error);
      total += count || 0;
      console.log(`   - events.apparitions (orgId): ${count || 0}`);
    }

    // === Niveau 2 : analytics_daily (1-30 jours) ===
    let dailySum = 0;
    if (eventIds && eventIds.length > 0) {
      // Query by event_id (new data + non-series)
      const dailyByEvent = supabase
        .from('analytics_daily')
        .select('apparitions')
        .in('event_id', eventIds);
      if (startDate && endDate) dailyByEvent.gte('date', startDate).lte('date', endDate);
      const { data: dailyEventData, error: dailyEventError } = await dailyByEvent;
      if (dailyEventError) console.error('❌ Erreur daily apparitions (eventId):', dailyEventError);
      dailySum += dailyEventData?.reduce((sum: number, row: any) => sum + (row.apparitions || 0), 0) || 0;

      // Also query by event_series_id for historical data (event_id=NULL)
      const seriesIds = await this.getSeriesIdsFromEventIds(eventIds);
      if (seriesIds.length > 0) {
        const dailyBySeries = supabase
          .from('analytics_daily')
          .select('apparitions, event_series_id')
          .in('event_series_id', seriesIds)
          .is('event_id', null);
        if (startDate && endDate) dailyBySeries.gte('date', startDate).lte('date', endDate);
        const { data: dailySeriesData, error: dailySeriesError } = await dailyBySeries;
        if (dailySeriesError) console.error('❌ Erreur daily apparitions (series):', dailySeriesError);
        // Distribute series data proportionally (divide by events in series that match our eventIds)
        if (dailySeriesData && dailySeriesData.length > 0) {
          const seriesSum = dailySeriesData.reduce((sum: number, row: any) => sum + (row.apparitions || 0), 0);
          const seriesEventCount = await this.getSeriesEventCount(seriesIds);
          const proportion = eventIds.length / Math.max(seriesEventCount, 1);
          dailySum += Math.round(seriesSum * proportion);
        }
      }
    } else {
      const dailyQuery = supabase
        .from('analytics_daily')
        .select('apparitions')
        .eq('organizer_id', organizerId);
      if (startDate && endDate) dailyQuery.gte('date', startDate).lte('date', endDate);
      const { data: dailyData, error: dailyError } = await dailyQuery;
      if (dailyError) console.error('❌ Erreur daily apparitions:', dailyError);
      dailySum = dailyData?.reduce((sum: number, row: any) => sum + (row.apparitions || 0), 0) || 0;
    }
    total += dailySum;
    console.log(`   - daily.apparitions: ${dailySum}`);

    // === Niveau 3 : analytics_alltime (> 30 jours) ===
    let alltimeSum = 0;
    if (eventIds && eventIds.length > 0) {
      // Query by event_id
      let alltimeByEvent = supabase
        .from('analytics_alltime')
        .select('apparitions, first_date, last_date')
        .in('event_id', eventIds);
      if (startDate && endDate) alltimeByEvent = alltimeByEvent.lte('first_date', endDate).gte('last_date', startDate);
      const { data: alltimeEventData, error: alltimeEventError } = await alltimeByEvent;
      if (alltimeEventError) console.error('❌ Erreur alltime apparitions (eventId):', alltimeEventError);
      alltimeSum += alltimeEventData?.reduce((sum: number, row: any) => sum + (row.apparitions || 0), 0) || 0;

      // Also query by series_id for historical data
      const seriesIds = await this.getSeriesIdsFromEventIds(eventIds);
      if (seriesIds.length > 0) {
        let alltimeBySeries = supabase
          .from('analytics_alltime')
          .select('apparitions, first_date, last_date, event_series_id')
          .in('event_series_id', seriesIds)
          .is('event_id', null);
        if (startDate && endDate) alltimeBySeries = alltimeBySeries.lte('first_date', endDate).gte('last_date', startDate);
        const { data: alltimeSeriesData, error: alltimeSeriesError } = await alltimeBySeries;
        if (alltimeSeriesError) console.error('❌ Erreur alltime apparitions (series):', alltimeSeriesError);
        if (alltimeSeriesData && alltimeSeriesData.length > 0) {
          const seriesSum = alltimeSeriesData.reduce((sum: number, row: any) => sum + (row.apparitions || 0), 0);
          const seriesEventCount = await this.getSeriesEventCount(seriesIds);
          const proportion = eventIds.length / Math.max(seriesEventCount, 1);
          alltimeSum += Math.round(seriesSum * proportion);
        }
      }
    } else {
      let alltimeQuery = supabase
        .from('analytics_alltime')
        .select('apparitions, first_date, last_date')
        .eq('organizer_id', organizerId);
      if (startDate && endDate) alltimeQuery = alltimeQuery.lte('first_date', endDate).gte('last_date', startDate);
      const { data: alltimeData, error: alltimeError } = await alltimeQuery;
      if (alltimeError) console.error('❌ Erreur alltime apparitions:', alltimeError);
      alltimeSum = alltimeData?.reduce((sum: number, row: any) => sum + (row.apparitions || 0), 0) || 0;
    }
    total += alltimeSum;
    console.log(`   - alltime.apparitions: ${alltimeSum}`);

    console.log(`👁️ [3-tier] Apparitions TOTAL: ${total}`);
    return total;
  }

  /**
   * KPI 3: Clics sur les événements de l'organisateur
   * Utilise eventIds si disponible (plus fiable), sinon fallback sur organizer_id
   * ✅ NOUVEAU : Requête sur 3 niveaux (hot/warm/cold)
   */
  private static async getEventClicks(
    organizerId: string,
    startDate: string | null,
    endDate: string | null,
    eventIds?: string[]
  ): Promise<number> {

    let total = 0;

    // === Niveau 1 : analytics_events (< 24h) ===
    if (eventIds && eventIds.length > 0) {
      let query = supabase
        .from('analytics_events')
        .select('*', { count: 'exact', head: true })
        .eq('event_name', 'clic_evenement')
        .in('event_id', eventIds);

      if (startDate && endDate) {
        query = query.gte('day_key', startDate).lte('day_key', endDate);
      }

      const { count, error } = await query;
      if (error) console.error('❌ Erreur events clics (by eventIds):', error);
      total += count || 0;
      console.log(`   - events.clics (eventIds): ${count || 0}`);
    } else {
      let query = supabase
        .from('analytics_events')
        .select('*', { count: 'exact', head: true })
        .eq('event_name', 'clic_evenement')
        .eq('organizer_id', organizerId);

      if (startDate && endDate) {
        query = query.gte('day_key', startDate).lte('day_key', endDate);
      }

      const { count, error } = await query;
      if (error) console.error('❌ Erreur events clics (by organizerId):', error);
      total += count || 0;
      console.log(`   - events.clics (orgId): ${count || 0}`);
    }

    // === Niveau 2 : analytics_daily (1-30 jours) ===
    let dailySum = 0;
    if (eventIds && eventIds.length > 0) {
      // Query by event_id (new data + non-series)
      const dailyByEvent = supabase
        .from('analytics_daily')
        .select('clics')
        .in('event_id', eventIds);
      if (startDate && endDate) dailyByEvent.gte('date', startDate).lte('date', endDate);
      const { data: dailyEventData, error: dailyEventError } = await dailyByEvent;
      if (dailyEventError) console.error('❌ Erreur daily clics (eventId):', dailyEventError);
      dailySum += dailyEventData?.reduce((sum: number, row: any) => sum + (row.clics || 0), 0) || 0;

      // Also query by event_series_id for historical data (event_id=NULL)
      const seriesIds = await this.getSeriesIdsFromEventIds(eventIds);
      if (seriesIds.length > 0) {
        const dailyBySeries = supabase
          .from('analytics_daily')
          .select('clics, event_series_id')
          .in('event_series_id', seriesIds)
          .is('event_id', null);
        if (startDate && endDate) dailyBySeries.gte('date', startDate).lte('date', endDate);
        const { data: dailySeriesData, error: dailySeriesError } = await dailyBySeries;
        if (dailySeriesError) console.error('❌ Erreur daily clics (series):', dailySeriesError);
        if (dailySeriesData && dailySeriesData.length > 0) {
          const seriesSum = dailySeriesData.reduce((sum: number, row: any) => sum + (row.clics || 0), 0);
          const seriesEventCount = await this.getSeriesEventCount(seriesIds);
          const proportion = eventIds.length / Math.max(seriesEventCount, 1);
          dailySum += Math.round(seriesSum * proportion);
        }
      }
    } else {
      const dailyQuery = supabase
        .from('analytics_daily')
        .select('clics')
        .eq('organizer_id', organizerId);
      if (startDate && endDate) dailyQuery.gte('date', startDate).lte('date', endDate);
      const { data: dailyData, error: dailyError } = await dailyQuery;
      if (dailyError) console.error('❌ Erreur daily clics:', dailyError);
      dailySum = dailyData?.reduce((sum: number, row: any) => sum + (row.clics || 0), 0) || 0;
    }
    total += dailySum;
    console.log(`   - daily.clics: ${dailySum}`);

    // === Niveau 3 : analytics_alltime (> 30 jours) ===
    let alltimeSum = 0;
    if (eventIds && eventIds.length > 0) {
      // Query by event_id
      let alltimeByEvent = supabase
        .from('analytics_alltime')
        .select('clics, first_date, last_date')
        .in('event_id', eventIds);
      if (startDate && endDate) alltimeByEvent = alltimeByEvent.lte('first_date', endDate).gte('last_date', startDate);
      const { data: alltimeEventData, error: alltimeEventError } = await alltimeByEvent;
      if (alltimeEventError) console.error('❌ Erreur alltime clics (eventId):', alltimeEventError);
      alltimeSum += alltimeEventData?.reduce((sum: number, row: any) => sum + (row.clics || 0), 0) || 0;

      // Also query by series_id for historical data
      const seriesIds = await this.getSeriesIdsFromEventIds(eventIds);
      if (seriesIds.length > 0) {
        let alltimeBySeries = supabase
          .from('analytics_alltime')
          .select('clics, first_date, last_date, event_series_id')
          .in('event_series_id', seriesIds)
          .is('event_id', null);
        if (startDate && endDate) alltimeBySeries = alltimeBySeries.lte('first_date', endDate).gte('last_date', startDate);
        const { data: alltimeSeriesData, error: alltimeSeriesError } = await alltimeBySeries;
        if (alltimeSeriesError) console.error('❌ Erreur alltime clics (series):', alltimeSeriesError);
        if (alltimeSeriesData && alltimeSeriesData.length > 0) {
          const seriesSum = alltimeSeriesData.reduce((sum: number, row: any) => sum + (row.clics || 0), 0);
          const seriesEventCount = await this.getSeriesEventCount(seriesIds);
          const proportion = eventIds.length / Math.max(seriesEventCount, 1);
          alltimeSum += Math.round(seriesSum * proportion);
        }
      }
    } else {
      let alltimeQuery = supabase
        .from('analytics_alltime')
        .select('clics, first_date, last_date')
        .eq('organizer_id', organizerId);
      if (startDate && endDate) alltimeQuery = alltimeQuery.lte('first_date', endDate).gte('last_date', startDate);
      const { data: alltimeData, error: alltimeError } = await alltimeQuery;
      if (alltimeError) console.error('❌ Erreur alltime clics:', alltimeError);
      alltimeSum = alltimeData?.reduce((sum: number, row: any) => sum + (row.clics || 0), 0) || 0;
    }
    total += alltimeSum;
    console.log(`   - alltime.clics: ${alltimeSum}`);

    console.log(`🖱️ [3-tier] Clics TOTAL: ${total}`);
    return total;
  }

  /**
   * KPI 4: Likes sur les événements de l'organisateur
   * Utilise eventIds si disponible (plus fiable), sinon fallback sur organizer_id
   * ✅ NOUVEAU : Requête sur 3 niveaux (hot/warm/cold)
   */
  private static async getEventLikes(
    organizerId: string,
    startDate: string | null,
    endDate: string | null,
    eventIds?: string[]
  ): Promise<number> {

    let total = 0;

    // === Niveau 1 : analytics_events (< 24h) ===
    if (eventIds && eventIds.length > 0) {
      let query = supabase
        .from('analytics_events')
        .select('*', { count: 'exact', head: true })
        .eq('event_name', 'like_evenement')
        .in('event_id', eventIds);

      if (startDate && endDate) {
        query = query.gte('day_key', startDate).lte('day_key', endDate);
      }

      const { count, error } = await query;
      if (error) console.error('❌ Erreur events likes (by eventIds):', error);
      total += count || 0;
      console.log(`   - events.likes (eventIds): ${count || 0}`);
    } else {
      let query = supabase
        .from('analytics_events')
        .select('*', { count: 'exact', head: true })
        .eq('event_name', 'like_evenement')
        .eq('organizer_id', organizerId);

      if (startDate && endDate) {
        query = query.gte('day_key', startDate).lte('day_key', endDate);
      }

      const { count, error } = await query;
      if (error) console.error('❌ Erreur events likes (by organizerId):', error);
      total += count || 0;
      console.log(`   - events.likes (orgId): ${count || 0}`);
    }

    // === Niveau 2 : analytics_daily (1-30 jours) ===
    let dailySum = 0;
    if (eventIds && eventIds.length > 0) {
      // Query by event_id (new data + non-series)
      const dailyByEvent = supabase
        .from('analytics_daily')
        .select('likes')
        .in('event_id', eventIds);
      if (startDate && endDate) dailyByEvent.gte('date', startDate).lte('date', endDate);
      const { data: dailyEventData, error: dailyEventError } = await dailyByEvent;
      if (dailyEventError) console.error('❌ Erreur daily likes (eventId):', dailyEventError);
      dailySum += dailyEventData?.reduce((sum: number, row: any) => sum + (row.likes || 0), 0) || 0;

      // Also query by event_series_id for historical data (event_id=NULL)
      const seriesIds = await this.getSeriesIdsFromEventIds(eventIds);
      if (seriesIds.length > 0) {
        const dailyBySeries = supabase
          .from('analytics_daily')
          .select('likes, event_series_id')
          .in('event_series_id', seriesIds)
          .is('event_id', null);
        if (startDate && endDate) dailyBySeries.gte('date', startDate).lte('date', endDate);
        const { data: dailySeriesData, error: dailySeriesError } = await dailyBySeries;
        if (dailySeriesError) console.error('❌ Erreur daily likes (series):', dailySeriesError);
        if (dailySeriesData && dailySeriesData.length > 0) {
          const seriesSum = dailySeriesData.reduce((sum: number, row: any) => sum + (row.likes || 0), 0);
          const seriesEventCount = await this.getSeriesEventCount(seriesIds);
          const proportion = eventIds.length / Math.max(seriesEventCount, 1);
          dailySum += Math.round(seriesSum * proportion);
        }
      }
    } else {
      const dailyQuery = supabase
        .from('analytics_daily')
        .select('likes')
        .eq('organizer_id', organizerId);
      if (startDate && endDate) dailyQuery.gte('date', startDate).lte('date', endDate);
      const { data: dailyData, error: dailyError } = await dailyQuery;
      if (dailyError) console.error('❌ Erreur daily likes:', dailyError);
      dailySum = dailyData?.reduce((sum: number, row: any) => sum + (row.likes || 0), 0) || 0;
    }
    total += dailySum;
    console.log(`   - daily.likes: ${dailySum}`);

    // === Niveau 3 : analytics_alltime (> 30 jours) ===
    let alltimeSum = 0;
    if (eventIds && eventIds.length > 0) {
      // Query by event_id
      let alltimeByEvent = supabase
        .from('analytics_alltime')
        .select('likes, first_date, last_date')
        .in('event_id', eventIds);
      if (startDate && endDate) alltimeByEvent = alltimeByEvent.lte('first_date', endDate).gte('last_date', startDate);
      const { data: alltimeEventData, error: alltimeEventError } = await alltimeByEvent;
      if (alltimeEventError) console.error('❌ Erreur alltime likes (eventId):', alltimeEventError);
      alltimeSum += alltimeEventData?.reduce((sum: number, row: any) => sum + (row.likes || 0), 0) || 0;

      // Also query by series_id for historical data
      const seriesIds = await this.getSeriesIdsFromEventIds(eventIds);
      if (seriesIds.length > 0) {
        let alltimeBySeries = supabase
          .from('analytics_alltime')
          .select('likes, first_date, last_date, event_series_id')
          .in('event_series_id', seriesIds)
          .is('event_id', null);
        if (startDate && endDate) alltimeBySeries = alltimeBySeries.lte('first_date', endDate).gte('last_date', startDate);
        const { data: alltimeSeriesData, error: alltimeSeriesError } = await alltimeBySeries;
        if (alltimeSeriesError) console.error('❌ Erreur alltime likes (series):', alltimeSeriesError);
        if (alltimeSeriesData && alltimeSeriesData.length > 0) {
          const seriesSum = alltimeSeriesData.reduce((sum: number, row: any) => sum + (row.likes || 0), 0);
          const seriesEventCount = await this.getSeriesEventCount(seriesIds);
          const proportion = eventIds.length / Math.max(seriesEventCount, 1);
          alltimeSum += Math.round(seriesSum * proportion);
        }
      }
    } else {
      let alltimeQuery = supabase
        .from('analytics_alltime')
        .select('likes, first_date, last_date')
        .eq('organizer_id', organizerId);
      if (startDate && endDate) alltimeQuery = alltimeQuery.lte('first_date', endDate).gte('last_date', startDate);
      const { data: alltimeData, error: alltimeError } = await alltimeQuery;
      if (alltimeError) console.error('❌ Erreur alltime likes:', alltimeError);
      alltimeSum = alltimeData?.reduce((sum: number, row: any) => sum + (row.likes || 0), 0) || 0;
    }
    total += alltimeSum;
    console.log(`   - alltime.likes: ${alltimeSum}`);

    console.log(`❤️ [3-tier] Likes TOTAL: ${total}`);
    return total;
  }

  /**
   * Helper: Get series_ids for events that belong to a series
   * Returns unique series_ids from the events table for given eventIds
   */
  private static _seriesCache: Map<string, string[]> = new Map();

  private static async getSeriesIdsFromEventIds(eventIds: string[]): Promise<string[]> {
    const cacheKey = eventIds.sort().join(',');
    if (this._seriesCache.has(cacheKey)) {
      return this._seriesCache.get(cacheKey)!;
    }

    const { data, error } = await supabase
      .from('events')
      .select('series_id')
      .in('id', eventIds)
      .not('series_id', 'is', null);

    if (error) {
      console.error('❌ Erreur getSeriesIdsFromEventIds:', error);
      return [];
    }

    const seriesIds = Array.from(new Set((data || []).map((e: any) => e.series_id as string)));
    this._seriesCache.set(cacheKey, seriesIds);
    return seriesIds;
  }

  /**
   * Helper: Get total number of events across all given series
   * Used to proportionally distribute series-level analytics to individual events
   */
  private static _seriesCountCache: Map<string, number> = new Map();

  private static async getSeriesEventCount(seriesIds: string[]): Promise<number> {
    const cacheKey = seriesIds.sort().join(',');
    if (this._seriesCountCache.has(cacheKey)) {
      return this._seriesCountCache.get(cacheKey)!;
    }

    const { count, error } = await supabase
      .from('events')
      .select('*', { count: 'exact', head: true })
      .in('series_id', seriesIds);

    if (error) {
      console.error('❌ Erreur getSeriesEventCount:', error);
      return 1;
    }

    const result = count || 1;
    this._seriesCountCache.set(cacheKey, result);
    return result;
  }

  /**
   * KPI 5: Nombre d'événements publiés
   * NOTE: Cette méthode est appelée uniquement si publishedEventsCount n'est pas fourni.
   * Avec la nouvelle architecture, le count est toujours passé depuis OrganizerContext.
   */
  private static async getPublishedEventsCount(organizerId: string): Promise<number> {
    console.log(`⚠️ [OrganizerAnalyticsService] getPublishedEventsCount appelé sans count pré-calculé pour ${organizerId}`);
    // Retourner 0 par défaut - le count devrait toujours être passé via publishedEventsCount
    return 0;
  }

  /**
   * Calculer les tendances (comparaison période actuelle vs précédente)
   */
  private static calculateTrends(
    current: DashboardKPIs, 
    previous: DashboardKPIs
  ): DashboardData['trends'] {
    
    const calculateTrend = (currentValue: number, previousValue: number) => {
      if (previousValue === 0) {
        return { value: currentValue > 0 ? 100 : 0, type: 'up' as const };
      }
      
      const percentChange = ((currentValue - previousValue) / previousValue) * 100;
      return {
        value: Math.abs(Math.round(percentChange)),
        type: percentChange >= 0 ? 'up' as const : 'down' as const
      };
    };

    return {
      connectionsZone: (current.connectionsZone !== null && previous.connectionsZone !== null)
        ? calculateTrend(current.connectionsZone, previous.connectionsZone)
        : { value: 0, type: 'up' as const },
      apparitionsEvents: calculateTrend(current.apparitionsEvents, previous.apparitionsEvents),
      clicsEvents: calculateTrend(current.clicsEvents, previous.clicsEvents),
      likesEvents: calculateTrend(current.likesEvents, previous.likesEvents)
    };
  }

  /**
   * Retourner des tendances par défaut (pour all time où il n'y a pas de comparaison)
   */
  private static getDefaultTrends(): DashboardData['trends'] {
    return {
      connectionsZone: { value: 0, type: 'up' as const },
      apparitionsEvents: { value: 0, type: 'up' as const },
      clicsEvents: { value: 0, type: 'up' as const },
      likesEvents: { value: 0, type: 'up' as const }
    };
  }

  /**
   * Générer des insights automatiques basés sur les données
   */
  private static generateInsights(
    kpis: DashboardKPIs,
    trends: DashboardData['trends'],
    hasZone: boolean = true
  ): DashboardData['insights'] {

    const insights: DashboardData['insights'] = [];

    // Insight prioritaire : Zone non configurée
    if (!hasZone) {
      insights.push({
        type: 'conseil',
        title: '📍 Configurez votre zone',
        message: 'Renseignez votre adresse dans votre profil pour voir les connexions dans votre zone.'
      });
    }

    // Insight 1: Activité forte dans la zone (seulement si zone configurée)
    if (hasZone && kpis.connectionsZone !== null && kpis.connectionsZone > this.CONFIG.THRESHOLDS.HIGH_ACTIVITY) {
      insights.push({
        type: 'insight',
        title: '🔥 Zone très active !',
        message: `${kpis.connectionsZone} personnes se sont connectées dans votre zone cette semaine.`
      });
    }

    // Insight 2: Conversion faible (seulement si zone configurée)
    const connectionsForRate = kpis.connectionsZone ?? 0;
    const conversionRate = connectionsForRate > 0 ? kpis.clicsEvents / connectionsForRate : 0;
    if (hasZone && conversionRate < this.CONFIG.THRESHOLDS.LOW_CONVERSION && connectionsForRate > 10) {
      insights.push({
        type: 'conseil',
        title: '💡 Optimisation possible',
        message: 'Votre taux de conversion est faible. Essayez d\'améliorer vos photos et descriptions !'
      });
    }

    // Insight 3: Tendance positive forte
    if (trends.apparitionsEvents.type === 'up' && trends.apparitionsEvents.value > 50) {
      insights.push({
        type: 'insight',
        title: '📈 Excellente progression !',
        message: `Vos événements apparaissent ${trends.apparitionsEvents.value}% plus souvent que la semaine dernière.`
      });
    }

    // Insight 4: Engagement fort
    const engagementRate = kpis.apparitionsEvents > 0 ? kpis.likesEvents / kpis.apparitionsEvents : 0;
    if (engagementRate > 0.1) {
      insights.push({
        type: 'insight',
        title: '❤️ Contenu apprécié !',
        message: 'Votre taux d\'engagement est excellent, continuez sur cette voie !'
      });
    }

    // Si pas d'insights spécifiques, ajouter des conseils génériques
    if (insights.length === 0) {
      insights.push(
        {
          type: 'conseil',
          title: '🎯 Conseil du jour',
          message: 'Publiez vos événements 48h à l\'avance pour maximiser la visibilité.'
        },
        {
          type: 'insight',
          title: '📊 Suivi régulier',
          message: 'Consultez votre dashboard quotidiennement pour optimiser vos performances.'
        }
      );
    }

    return insights.slice(0, 2); // Limiter à 2 insights max
  }

  /**
   * Vérifier/créer la zone de l'organisateur
   * Auto-crée la zone depuis le profil organisateur Supabase si lat/lng existe
   */
  private static async ensureOrganizerZone(organizerId: string): Promise<OrganizerZone | null> {
    try {
      // 1. Vérifier si la zone existe déjà
      // Note: .maybeSingle() au lieu de .single() pour éviter erreur 406 si aucune zone n'existe
      const { data: existingZone, error: zoneError } = await supabase
        .from('organizer_zones')
        .select('*')
        .eq('organizer_id', organizerId)
        .maybeSingle();

      if (!zoneError && existingZone) {
        console.log(`✅ Zone organisateur trouvée:`, existingZone);
        return existingZone;
      }

      // 2. Si pas de zone, essayer de la créer depuis le profil organisateur
      console.log(`ℹ️ Zone non trouvée, tentative de création depuis le profil...`);

      const { data: organizer, error: orgError } = await supabase
        .from('organizers')
        .select('id, latitude, longitude, location_name')
        .eq('id', organizerId)
        .single();

      if (orgError || !organizer) {
        console.log(`⚠️ Profil organisateur non trouvé pour ${organizerId}`);
        return null;
      }

      // 3. Vérifier si le profil a des coordonnées
      if (!organizer.latitude || !organizer.longitude) {
        console.log(`⚠️ Profil organisateur ${organizerId} sans coordonnées - zone non créée`);
        return null;
      }

      // 4. Créer automatiquement la zone
      console.log(`🔧 Création automatique de la zone depuis le profil...`);
      const newZone = await this.createOrganizerZone(
        organizerId,
        organizer.latitude,
        organizer.longitude,
        organizer.location_name || undefined
      );

      if (newZone) {
        console.log(`✅ Zone organisateur créée automatiquement:`, newZone);
      }

      return newZone;

    } catch (error) {
      console.error('❌ Erreur vérification/création zone:', error);
      return null;
    }
  }

  /**
   * Créer la zone organisateur à partir de son adresse
   * Utilise le geohash pour l'indexation spatiale
   */
  static async createOrganizerZone(
    organizerId: string,
    latitude: number,
    longitude: number,
    address?: string
  ): Promise<OrganizerZone | null> {

    try {
      const geohashValue = generateGeohash(latitude, longitude);

      // Supprimer les anciennes zones pour éviter les doublons
      await supabase
        .from('organizer_zones')
        .delete()
        .eq('organizer_id', organizerId)
        .neq('geohash', geohashValue);

      const newZone: Omit<OrganizerZone, 'updated_at'> = {
        organizer_id: organizerId,
        geohash: geohashValue,
        latitude,
        longitude,
        address
      };

      // TODO: Supprimer 'as any' après génération des types via: npm run supabase:types
      const { data, error } = await supabase
        .from('organizer_zones')
        .upsert([newZone] as any, {
          onConflict: 'organizer_id,geohash'
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      console.log(`✅ Zone organisateur créée/mise à jour:`, data);
      return data;

    } catch (error) {
      console.error('❌ Erreur création zone:', error);
      return null;
    }
  }

  /**
   * S'abonner aux mises à jour temps réel du dashboard
   * Inclut la vérification de zone pour les connexions
   */
  static subscribeToRealtimeUpdates(
    organizerId: string,
    callback: (data: Partial<DashboardKPIs>) => void
  ): () => void {

    console.log(`🔄 Abonnement temps réel pour organisateur: ${organizerId}`);

    // Cache pour la zone organisateur (évite requêtes répétées)
    let cachedOrganizerZone: OrganizerZone | null = null;
    let zoneLoaded = false;

    // Charger la zone organisateur au démarrage
    // Note: .maybeSingle() au lieu de .single() pour éviter erreur 406 si aucune zone n'existe
    const loadOrganizerZone = async () => {
      try {
        const { data: zone } = await supabase
          .from('organizer_zones')
          .select('*')
          .eq('organizer_id', organizerId)
          .maybeSingle();

        cachedOrganizerZone = zone || null;
        zoneLoaded = true;

        if (cachedOrganizerZone) {
          console.log(`📍 Zone organisateur chargée: ${cachedOrganizerZone.geohash.substring(0, 5)}*`);
        } else {
          console.log(`⚠️ Pas de zone configurée pour ${organizerId}`);
        }
      } catch (error) {
        console.error('❌ Erreur chargement zone:', error);
        zoneLoaded = true;
      }
    };

    // Démarrer le chargement de la zone
    loadOrganizerZone();

    try {
      const subscription = supabase
        .channel('organizer_dashboard')
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'analytics_events',
          filter: `organizer_id=eq.${organizerId}`
        }, (payload) => {
          console.log('⚡ Nouvel événement temps réel:', payload.new);

          // Mettre à jour le KPI correspondant
          const eventName = (payload.new as AnalyticsEvent).event_name;
          const update: Partial<DashboardKPIs> = {};

          switch (eventName) {
            case 'apparition_evenement':
              update.apparitionsEvents = 1; // +1
              break;
            case 'clic_evenement':
              update.clicsEvents = 1; // +1
              break;
            case 'like_evenement':
              update.likesEvents = 1; // +1
              break;
          }

          if (Object.keys(update).length > 0) {
            callback(update);
          }
        })
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'analytics_events',
          filter: `event_name=eq.connexion_zone`
        }, (payload) => {
          // Vérifier si la connexion est dans la zone de l'organisateur
          const eventGeohash = (payload.new as AnalyticsEvent).geohash;

          if (!zoneLoaded) {
            console.log('⏳ Zone pas encore chargée, connexion ignorée');
            return;
          }

          if (!cachedOrganizerZone || !eventGeohash) {
            return;
          }

          // Comparer les préfixes geohash (précision 5 = ~4.9km)
          const zonePrefix = cachedOrganizerZone.geohash.substring(0, 5);
          const eventPrefix = eventGeohash.substring(0, 5);

          if (eventPrefix === zonePrefix) {
            console.log(`⚡ Nouvelle connexion zone détectée: ${eventPrefix}`);
            callback({ connectionsZone: 1 }); // +1
          }
        })
        .subscribe((status, err) => {
          if (status === 'SUBSCRIBED') {
            console.log('✅ Abonnement temps réel actif');
          } else if (status === 'CLOSED') {
            console.log('🔌 Abonnement temps réel fermé');
          } else if (status === 'CHANNEL_ERROR') {
            console.error('❌ Erreur canal temps réel:', err);
          }
        });

      // Retourner la fonction de cleanup
      return () => {
        console.log('🔌 Désabonnement temps réel');
        try {
          supabase.removeChannel(subscription);
        } catch (error) {
          console.error('⚠️ Erreur lors du désabonnement:', error);
        }
      };
    } catch (error) {
      console.error('❌ Erreur configuration abonnement temps réel:', error);

      // Retourner une fonction de cleanup vide
      return () => {
        console.log('🔌 Cleanup vide (pas d\'abonnement actif)');
      };
    }
  }

  /**
   * Données par défaut en cas d'erreur
   */
  private static getDefaultDashboardData(): DashboardData {
    return {
      kpis: {
        connectionsZone: null,
        apparitionsEvents: 0,
        clicsEvents: 0,
        likesEvents: 0,
        eventsPublies: 0,
        hasZone: false
      },
      trends: {
        connectionsZone: { value: 0, type: 'up' },
        apparitionsEvents: { value: 0, type: 'up' },
        clicsEvents: { value: 0, type: 'up' },
        likesEvents: { value: 0, type: 'up' }
      },
      insights: [
        {
          type: 'conseil',
          title: '🚀 Premiers pas',
          message: 'Publiez votre premier événement pour voir vos statistiques !'
        },
        {
          type: 'conseil',
          title: '💡 Optimisation',
          message: 'Utilisez des photos de qualité pour attirer plus de clics.'
        }
      ]
    };
  }

  /**
   * Méthodes utilitaires pour les tests
   */
  
  /**
   * Insérer des données de test
   */
  static async insertTestData(organizerId: string, geohash: string): Promise<void> {
    const testEvents: AnalyticsEvent[] = [];
    const now = new Date();

    // Générer des événements test pour les 7 derniers jours
    for (let i = 0; i < 7; i++) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);

      const dayKey = date.toISOString().split('T')[0];
      const weekKey = `${date.getFullYear()}-W${Math.ceil(date.getDate() / 7)}`;

      // Connexions zone (10-50 par jour)
      // Migration 075 : la policy RLS analytics_events_insert_validated n'accepte plus
      // 'debut_session' (mort depuis la migration 045) — on utilise 'connexion_zone',
      // sinon l'insert échoue en bloc (batch atomique).
      const connectionsCount = Math.floor(Math.random() * 40) + 10;
      for (let j = 0; j < connectionsCount; j++) {
        testEvents.push({
          event_name: 'connexion_zone',
          geohash: geohash,
          day_key: dayKey,
          week_key: weekKey,
          metadata: { user_type: 'touriste', test: true }
        });
      }

      // Apparitions événement (50-200 par jour)
      const apparitionsCount = Math.floor(Math.random() * 150) + 50;
      for (let j = 0; j < apparitionsCount; j++) {
        testEvents.push({
          event_name: 'apparition_evenement',
          geohash: geohash,
          organizer_id: organizerId,
          event_id: `event_test_${j % 5}`,
          day_key: dayKey,
          week_key: weekKey,
          metadata: { test: true }
        });
      }

      // Clics (10-50 par jour)
      const clicsCount = Math.floor(Math.random() * 40) + 10;
      for (let j = 0; j < clicsCount; j++) {
        testEvents.push({
          event_name: 'clic_evenement',
          geohash: geohash,
          organizer_id: organizerId,
          event_id: `event_test_${j % 5}`,
          day_key: dayKey,
          week_key: weekKey,
          metadata: { test: true }
        });
      }

      // Likes (5-25 par jour)
      const likesCount = Math.floor(Math.random() * 20) + 5;
      for (let j = 0; j < likesCount; j++) {
        testEvents.push({
          event_name: 'like_evenement',
          geohash: geohash,
          organizer_id: organizerId,
          event_id: `event_test_${j % 5}`,
          day_key: dayKey,
          week_key: weekKey,
          metadata: { test: true }
        });
      }
    }
    
    // Insérer par batch de 100
    const batchSize = 100;
    for (let i = 0; i < testEvents.length; i += batchSize) {
      const batch = testEvents.slice(i, i + batchSize);
      // TODO: Supprimer 'as any' après génération des types via: npm run supabase:types
      const { error } = await supabase
        .from('analytics_events')
        .insert(batch as any);

      if (error) {
        console.error(`❌ Erreur insertion batch ${i}:`, error);
      } else {
        console.log(`✅ Batch ${i}-${i + batch.length} inséré`);
      }
    }
    
    console.log(`🎉 ${testEvents.length} événements test insérés !`);
  }

  /**
   * Nettoyer les données de test
   */
  static async cleanupTestData(): Promise<void> {
    const { error } = await supabase
      .from('analytics_events')
      .delete()
      .eq('metadata->test', true);

    if (error) {
      console.error('❌ Erreur nettoyage test data:', error);
    } else {
      console.log('🧹 Données de test supprimées');
    }
  }
}